import { ErrorHandler, NgModule, inject, provideAppInitializer } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { BrowserModule } from '@angular/platform-browser';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { SharedModule } from './shared/shared.module';
import { CoreModule } from './core/core.module';
import { provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { ToastrModule } from 'ngx-toastr';

import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';

import { HttpClient } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

import { CameraComponent } from './feature/camera/camera.component';
import { WebcamModule } from 'ngx-webcam';
import { CookieService } from 'ngx-cookie-service';
import { environment } from 'src/environments/environment';

import { AbilityService } from '@casl/angular';
import { createAbility, AppAbility } from './shared/services/AppAbility';
import { MultiTranslateHttpLoader } from './core/loaders/multi-translate-http-loader';
import { ServiceWorkerModule } from '@angular/service-worker';
import { REPORT_OUTBOX_HANDLERS } from './projects/offline/report-outbox-handlers';
import { TASK_OUTBOX_HANDLERS } from './tasks/offline/task-outbox-handlers';
import { DOCUMENT_OUTBOX_HANDLERS } from './projects/offline/document-outbox-handlers';
import { PlatformModule } from '@angular/cdk/platform';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { extractError } from './core/services/monitoringService/monitoring';
import { AuthService } from './auth/services/auth.service';
import { AUTH_V2 } from './auth/services/auth-v2.token';
import { SessionService } from './core/services/sessionService/session.service';
import { APP_LOCALE_PROVIDERS } from './core/locale/app-locale';

export function HttpLoaderFactory(http: HttpClient) {
  return new MultiTranslateHttpLoader(http, [
    { prefix: "../assets/i18n/", suffix: ".json" },
    { prefix: "./assets/i18n/auth/", suffix: ".json" },
    { prefix: "./assets/i18n/contact-management/", suffix: ".json" },
    { prefix: "./assets/i18n/dashboard/", suffix: ".json" },
    { prefix: "./assets/i18n/notifications/", suffix: ".json" },
    { prefix: "./assets/i18n/organization/", suffix: ".json" },
    { prefix: "./assets/i18n/profile/", suffix: ".json" },
    { prefix: "./assets/i18n/projects/", suffix: ".json" },
    { prefix: "./assets/i18n/tasks/", suffix: ".json" }
  ]);
}

@NgModule({ declarations: [
        AppComponent,
        CameraComponent
    ],
    bootstrap: [
        AppComponent
    ], imports: [BrowserModule,
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        AppRoutingModule,
        SharedModule,
        CoreModule,
        BrowserAnimationsModule,
        ToastrModule.forRoot(),
        TranslateModule.forRoot({
            fallbackLang: 'de',
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient],
            }
        }),
        WebcamModule,
        ServiceWorkerModule.register('ngsw-worker.js', {
            enabled: environment.serviceWorker,
            registrationStrategy: 'registerImmediately'
        }),
        BrowserModule,
        PlatformModule,
        NgxExtendedPdfViewerModule], providers: [
        CookieService,
        // Swiss German dates (LOCALE_ID de-CH, «| date» = TT.MM.JJJJ; Tester-Feedback R1, Punkt 10/11)
        ...APP_LOCALE_PROVIDERS,
        { provide: AppAbility, useFactory: createAbility },
        AbilityService,
        // Offline E2 step 2: Baujournal handlers of the outbox (eager, the sync runs on every page)
        ...REPORT_OUTBOX_HANDLERS,
        ...TASK_OUTBOX_HANDLERS,
        ...DOCUMENT_OUTBOX_HANDLERS,
        { provide: ErrorHandler, useValue: Sentry.createErrorHandler({ showDialog: false, extractor: extractError }) },
        provideHttpClient(withInterceptorsFromDi(), withInterceptors([authInterceptor])),
        // OAuth Phase 1: Access Token still über das Cookie holen bzw. Legacy-Token tauschen (nur mit authV2;
        // wirft nie, höchstens 5 s). Danach findet AppComponent.checkRole() die Rolle im Profil-Cache.
        // FE-3: mit authV2 SessionService früh erzeugen, damit jeder Tab Logout-Broadcasts anderer Tabs empfängt
        // Sprache Deutsch aktiv setzen (bis 2026-10 tat das das entfernte Botato-Modul; fallbackLang allein reicht nicht)
        provideAppInitializer(() => {
            inject(TranslateService).use('de');
        }),
        provideAppInitializer(() => {
            if (inject(AUTH_V2)) inject(SessionService);
            // leftovers of an older SW version (dataGroup «api-cache») must not outlive the next session switch
            void inject(SessionService).clearApiCache();
            return inject(AuthService).bootstrap();
        })
    ] })
export class AppModule { }
